import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize from "rehype-sanitize";
import { formatLongDate } from "./voting/utils/dateUtils";
import { ChatLayout } from "./layout/ChatLayout";
import { ChatInput } from "./chat/ChatInput";
import { Sparkles, ArrowLeft } from "lucide-react";
import { geminiService, type PollSuggestion } from "../lib/ai/gemini";
import type { DatePollSuggestion } from "../lib/ai/products/date/DatePollService";
import type { FormPollSuggestion } from "../lib/ai/products/form/FormPollService";
import { pollFromSuggestion } from "../lib/poll/pollFromSuggestion";
// Types temporaires - à remplacer quand l'import fonctionnera
interface FormPollDraft {
  id: string;
  type: "form";
  title: string;
  questions: any[];
}
interface FormOption {
  id: string;
  label: string;
}
interface AnyFormQuestion {
  id: string;
  title: string;
  required?: boolean;
  type: string;
  options?: FormOption[];
  placeholder?: string;
  maxLength?: number;
}
import { groupConsecutiveDates } from "../lib/date-utils";
import type { Poll } from "../lib/pollStorage";
import PollCreator from "./PollCreator";
import { FormPollCreator } from "../exports"; // Import depuis exports/index.ts
// import FormPollCreator, {
//   type FormPollDraft,
//   type AnyFormQuestion,
//   type FormOption,
// } from "./polls/FormPollCreator"; // MIGRÉ vers apps/form-polls/src/components/
import { debounce } from "lodash";
import { useAutoSave } from "../hooks/useAutoSave";
import { useConversationResume } from "../hooks/useConversationResume";
import { ConversationService } from "../services/ConversationService";
import { QuotaService, type AuthIncentiveType } from "../services/QuotaService";
import { useQuota } from "../hooks/useQuota";
import AuthIncentiveModal from "./modals/AuthIncentiveModal";
import QuotaIndicator from "./ui/QuotaIndicator";
import { useNavigate, useLocation } from "react-router-dom";
import { conversationProtection } from "../services/ConversationProtection";
import { performanceMonitor } from "../services/PerformanceMonitor";
import { useInfiniteLoopProtection } from "../services/InfiniteLoopProtection";
import { handleError, ErrorFactory, logError } from "../lib/error-handling";
import { logger } from "../lib/logger";
import { ConversationProvider } from "./conversation/ConversationProvider";
import { useConversation } from "./conversation/ConversationProvider";
import type {
  ConversationMessage,
  MessageRole,
  Message,
} from "../types/conversation";
import { ThinkingBubble } from "./chat/ThinkingBubble";

// Type pour les messages (compatibilité)
type LocalMessage = Omit<ConversationMessage, "role"> & {
  role: string; // Utiliser string au lieu de MessageRole pour compatibilité
  isAI?: boolean; // Ajouter isAI pour compatibilité
};

// Fonction convertMessage pour compatibilité
const convertMessage = (message: LocalMessage): ConversationMessage => ({
  ...message,
  role: message.role as MessageRole, // Convertir string vers MessageRole
});
import {
  MAX_MESSAGES_PER_CONVERSATION,
  MAX_MODIFICATIONS_PER_POLL,
  isConversationMessageLimitReached,
} from "../constants/quotas";
import { PollModificationTracker } from "../services/PollModificationTracker";
import { useAuth } from "../contexts/AuthContext";
import TurnstileWidget, { type TurnstileWidgetHandle } from "./TurnstileWidget";
import { getCachedFingerprint } from "../lib/browserFingerprint";
import {
  generateConversationScope,
  secureGeminiService,
} from "../services/SecureGeminiService";

import {
  VoiceRecognitionState,
  VoiceRecognitionActions,
} from "../hooks/useVoiceRecognition";
import { useToast } from "../hooks/use-toast";
import { resolveVoiceRecognitionState } from "../lib/voice/resolveVoiceRecognitionState";

// Export type for ref usage
export interface GeminiChatHandle {
  // Methods that can be called from parent components
  submitMessage?: (message: string) => void;
  /** Conversation en cours (pour la relier au sondage publié). */
  getConversationId?: () => string | null;
}

interface GeminiChatInterfaceProps {
  onPollCreated?: (pollData: PollSuggestion) => void | Promise<void>;
  /**
   * Sondages de dates : appelé à chaque changement de la proposition par
   * l'IA (génération, modification, retour à une version, annulation) pour
   * l'appliquer directement dans l'éditeur. Quand il est fourni, la carte
   * n'affiche plus « Créer ce sondage » : la validation se fait dans
   * l'éditeur (« Finaliser la création »).
   */
  onProposalChange?: (poll: Poll) => void;
  onUserMessage?: () => void;
  resumeLastConversation?: boolean;
  hideStatusBar?: boolean;
  darkTheme?: boolean;
  voiceRecognition?:
    | boolean
    | (VoiceRecognitionState & VoiceRecognitionActions);
  pollType?: "date" | "form" | "availability" | "quizz";
}

// Clés i18n pour le libellé de type affiché dans la card de proposition.
// Couvre tous les types supportés (pas seulement single/multiple/text) afin
// qu'une question rating/nps/long-text ne soit pas étiquetée "Texte libre".
const QUESTION_TYPE_LABEL_KEYS: Record<string, string> = {
  single: "creator:question_type_single",
  multiple: "creator:question_type_multiple",
  text: "creator:question_type_text",
  "long-text": "creator:question_type_long_text",
  rating: "creator:question_type_rating",
  nps: "creator:question_type_nps",
  matrix: "creator:question_type_matrix",
  date: "creator:question_type_date",
};

// Carte de suggestion du chat IA (titre de sondage / groupe de dates dans une
// réponse) — suit désormais --primary/--card comme le reste de l'app (voir
// memoire ui-themes ; décision revue le 2026-09-08, n'est plus theme-invariant).
const GEMINI_SUGGESTION_CARD_CLASS = "bg-muted rounded-lg p-3 md:p-4";
const GEMINI_CARD_TITLE_CLASS =
  "font-medium text-foreground text-sm md:text-base leading-tight";
const GEMINI_CARD_SUBTEXT_CLASS =
  "mt-1.5 md:mt-2 text-xs md:text-sm text-muted-foreground";
const GEMINI_CARD_REQUIRED_BADGE_CLASS = "text-destructive ml-2";

// Fonction de conversion FormPollSuggestion (Gemini) → FormPollDraft (FormPollCreator)
const convertFormSuggestionToDraft = (
  suggestion: FormPollSuggestion,
): FormPollDraft => {
  const uid = () => Math.random().toString(36).slice(2, 10);

  const questions: AnyFormQuestion[] = suggestion.questions.map((q) => {
    console.log("🔍 Question Gemini:", q);
    const baseQuestion = {
      id: uid(),
      title: q.title,
      required: q.required,
      type: q.type,
    };

    if (q.type === "single" || q.type === "multiple") {
      console.log("🔍 Options brutes:", q.options);
      const options: FormOption[] = (q.options || [])
        .filter((opt) => opt && typeof opt === "string" && opt.trim())
        .map((opt) => ({
          id: uid(),
          label: opt.trim(),
        }));
      console.log("🔍 Options converties:", options);

      return {
        ...baseQuestion,
        type: q.type,
        options,
        ...(q.maxChoices && { maxChoices: q.maxChoices }),
      } as AnyFormQuestion;
    } else {
      // type === "text"
      return {
        ...baseQuestion,
        type: "text",
        ...(q.placeholder && { placeholder: q.placeholder }),
        ...(q.maxLength && { maxLength: q.maxLength }),
      } as AnyFormQuestion;
    }
  });

  return {
    id: uid(),
    type: "form",
    title: suggestion.title,
    questions,
  };
};

const GeminiChatInterface = forwardRef<
  GeminiChatHandle,
  GeminiChatInterfaceProps
>(
  (
    {
      onPollCreated,
      onProposalChange,
      onUserMessage,
      resumeLastConversation = false,
      hideStatusBar = false,
      darkTheme = false,
      voiceRecognition = true, // ✅ Activé par défaut si supporté
      pollType = "date",
    }: GeminiChatInterfaceProps,
    ref,
  ) => {
    const { t, i18n } = useTranslation(["creator", "common", "errors"]);
    // Utiliser les messages du Context pour la persistance
    const {
      messages,
      setMessages,
      currentPoll,
      dispatchPollAction,
      initialPrompt,
      modificationCount,
      setInitialPrompt,
      incrementModificationCount,
      resetModificationCount,
    } = useConversation();

    // Authentification : Turnstile et autres garde-fous anti-bot ne ciblent
    // que les utilisateurs anonymes.
    const { user } = useAuth();

    // Turnstile : si le serveur renvoie 403 TURNSTILE_INVALID/REQUIRED, on
    // bascule le widget en mode "always" (visible) pour que l'utilisateur
    // puisse résoudre le challenge sans avoir à rafraîchir la page.
    const [needsChallenge, setNeedsChallenge] = useState(false);
    const [showTurnstileModal, setShowTurnstileModal] = useState(false);
    const turnstileRef = useRef<TurnstileWidgetHandle>(null);
    // Mémorise le prompt qui a déclenché TURNSTILE_REQUIRED pour le rejouer
    // automatiquement une fois le captcha résolu (cf. onVerify du modal).
    const turnstileRetryInputRef = useRef<string | null>(null);

    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPollCreator, setShowPollCreator] = useState(false);
    const [isCreatingPoll, setIsCreatingPoll] = useState(false);
    // Vrai une fois le sondage créé via le bouton card. Pilote le libellé du
    // bouton ("Créer" → "Mettre à jour") puisque les clics suivants mettent à
    // jour le sondage existant au lieu d'en recréer un.
    const [pollAlreadyCreated, setPollAlreadyCreated] = useState(false);

    // Sondages de dates dans l'espace de travail : chaque réponse de l'IA est
    // appliquée directement dans l'éditeur (plus de bouton « Créer ce
    // sondage » intermédiaire). Le chat et l'éditeur ont chacun leur propre
    // état de sondage : cet appel explicite est la seule passerelle.
    const appliesToEditor = !!onProposalChange && pollType === "date";
    const applyProposalToEditor = useCallback(
      (poll: Poll) => {
        if (appliesToEditor) onProposalChange?.(poll);
      },
      [appliesToEditor, onProposalChange],
    );
    // Garde synchrone contre les clics rapides sur "Créer ce sondage".
    // isCreatingPoll (state) n'est pas à jour dans la closure entre deux
    // clics du même tick → sans ce ref, plusieurs createPoll partaient en
    // parallèle et créaient des sondages en double (cf. bug hunt 2026-05-28).
    const isCreatingPollRef = useRef(false);
    // Identifiant opaque de la conversation en cours, envoyé au serveur pour
    // son plafond de messages (#1412). Renouvelé quand le chat repart de zéro.
    const conversationScopeRef = useRef<string | null>(null);
    const [selectedPollData, setSelectedPollData] =
      useState<PollSuggestion | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<
      "unknown" | "connected" | "error" | "guest"
    >("unknown");

    // Auto-save and conversation resume hooks
    const navigate = useNavigate();
    const location = useLocation();
    const autoSave = useAutoSave({
      debug: true,
    });
    useImperativeHandle(
      ref,
      () => ({
        getConversationId: () =>
          autoSave.getRealConversationId() || autoSave.conversationId || null,
      }),
      [autoSave],
    );
    const conversationResume = useConversationResume();
    const quota = useQuota({ showAuthIncentives: true });
    const loopProtection = useInfiniteLoopProtection("gemini-chat-interface");
    const { toast } = useToast();

    // Objet fourni → tel quel ; `false` → micro coupé ; sinon auto-détection.
    const vrState = resolveVoiceRecognitionState(voiceRecognition);

    // Utiliser useRef pour persister les flags entre les re-rendus
    const hasShownOfflineMessage = useRef(false);
    const wasOffline = useRef(false);
    const reconnectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatInputRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const hasInitialized = useRef(false);
    const hasResumedConversation = useRef(false);
    const isInitializing = useRef(false);
    const initializationPromise = useRef<Promise<void> | null>(null);

    // Synchroniser la transcription vocale avec l'input
    const lastFinalTranscriptRef = useRef("");
    useEffect(() => {
      if (
        vrState.finalTranscript &&
        vrState.finalTranscript !== lastFinalTranscriptRef.current
      ) {
        const newText = vrState.finalTranscript.slice(
          lastFinalTranscriptRef.current.length,
        );
        if (newText) {
          setInputValue((prev) => {
            const needsSpace =
              prev && !prev.endsWith(" ") && !newText.startsWith(" ");
            return prev + (needsSpace ? " " : "") + newText;
          });
        }
        lastFinalTranscriptRef.current = vrState.finalTranscript;
      }

      // Si le transcript est réinitialisé, on réinitialise notre ref
      if (!vrState.finalTranscript) {
        lastFinalTranscriptRef.current = "";
      }
    }, [vrState.finalTranscript]);

    // Initialize new conversation
    const initializeNewConversation = useCallback(async () => {
      // Don't initialize if messages already exist (restored from localStorage)
      if (messages.length > 0) {
        return;
      }

      // Don't initialize if we already resumed a conversation
      if (hasResumedConversation.current) {
        return;
      }

      // Multi-layer protection against infinite loops
      if (!loopProtection.canExecute()) {
        return;
      }

      if (!conversationProtection.canCreateConversation()) {
        return;
      }

      // Prevent multiple simultaneous initialization attempts
      if (isInitializing.current) {
        // Initialization already in progress, waiting...
        if (initializationPromise.current) {
          await initializationPromise.current;
        }
        return;
      }

      isInitializing.current = true;
      conversationProtection.startCreation();
      performanceMonitor.trackConversationCreation();

      initializationPromise.current = (async () => {
        try {
          // Initializing new conversation
          // Ne pas ajouter de message de bienvenue ici pour que le message visuel s'affiche (messages.length === 0)

          setMessages([]);
          // Conversation initialized with empty messages - visual welcome will show

          conversationProtection.completeCreation("new-conversation");
          // Conversation initialized successfully
        } catch (error) {
          const processedError = handleError(
            error,
            {
              component: "GeminiChatInterface",
              operation: "initializeNewConversation",
            },
            "Erreur lors de l'initialisation de la conversation",
          );

          logError(processedError, {
            component: "GeminiChatInterface",
            operation: "initializeNewConversation",
          });

          conversationProtection.failCreation();
          performanceMonitor.trackError();

          // Show user-friendly error message
          if (
            error instanceof Error &&
            error.message.includes("Quota dépassé")
          ) {
            setMessages([
              {
                conversationId: "",
                id: "quota-error",
                content: t("creator:conversation_limit_reached"),
                role: "assistant",
                timestamp: new Date(),
              },
            ]);
          } else if (
            error instanceof Error &&
            error.message.includes("Conversation limit reached")
          ) {
            setMessages([
              {
                conversationId: "",
                id: "limit-error",
                content: t("creator:conversation_limit_reached"),
                role: "assistant",
                timestamp: new Date(),
              },
            ]);
          }
        } finally {
          isInitializing.current = false;
          initializationPromise.current = null;
        }
      })();

      await initializationPromise.current;
    }, [loopProtection, messages, setMessages, t]);

    const testGeminiConnection = useCallback(async () => {
      // Connection test in progress (reduced logging)
      try {
        // Pas d'appel réseau sous Playwright / E2E pour éviter le 401 qui
        // ferait échouer les smoke tests (l'Edge Function bloque les anon
        // sans token valide ; le test env n'en a pas).
        const isE2ETesting =
          typeof window !== "undefined" &&
          (window.navigator.userAgent.includes("Playwright") ||
            (window.navigator as Navigator & { webdriver?: boolean })
              .webdriver === true);
        if (isE2ETesting) {
          setConnectionStatus("connected");
          return;
        }

        // Depuis 2026-05-11, l'Edge Function accepte les requêtes anonymes
        // (anon key Bearer) avec quota guest_quotas et rate-limit IP strict.
        // On teste la connexion pour tous, authentifiés ou non.
        const isConnected = await geminiService.testConnection();
        const newStatus = isConnected ? "connected" : "error";
        // Only log connection changes, not every test result

        // Si l'IA était hors ligne et redevient disponible
        if (wasOffline.current && isConnected && connectionStatus === "error") {
          // Gemini reconnected - adding reconnection message
          setMessages((prev) => [
            ...prev,
            {
              conversationId: "",
              id: `reconnected-${Date.now()}`,
              content: t("creator:ai_reconnected"),
              role: "assistant",
              timestamp: new Date(),
            },
          ]);
          wasOffline.current = false;
          hasShownOfflineMessage.current = false;
        }

        setConnectionStatus(newStatus);

        if (!isConnected) {
          // Si l'IA est hors ligne et qu'on n'a pas encore montré le message
          if (!hasShownOfflineMessage.current) {
            // Gemini unavailable - adding error message
            setMessages((prev) => [
              ...prev,
              {
                conversationId: "",
                id: `connection-error-${Date.now()}`,
                content: t("creator:ai_unavailable_retrying"),
                role: "assistant",
                timestamp: new Date(),
              },
            ]);
            hasShownOfflineMessage.current = true;
            wasOffline.current = true;
          }

          // Nettoyer le timeout précédent s'il existe
          if (reconnectionTimeoutRef.current) {
            clearTimeout(reconnectionTimeoutRef.current);
          }

          // Réessayer dans 10 secondes
          reconnectionTimeoutRef.current = setTimeout(() => {
            testGeminiConnection();
          }, 10000);
        }
      } catch (error) {
        setConnectionStatus("error");

        const processedError = handleError(
          error,
          {
            component: "GeminiChatInterface",
            operation: "testConnection",
          },
          "Erreur de connexion à Gemini",
        );

        logError(processedError, {
          component: "GeminiChatInterface",
          operation: "testConnection",
        });

        // Afficher le message d'erreur seulement la première fois
        if (!hasShownOfflineMessage.current) {
          // Gemini unavailable - adding error message
          setMessages((prev) => [
            ...prev,
            {
              conversationId: "",
              id: `connection-error-${Date.now()}`,
              content: t("creator:ai_unavailable_retrying"),
              role: "assistant",
              timestamp: new Date(),
            },
          ]);
          hasShownOfflineMessage.current = true;
          wasOffline.current = true;
        }

        // Nettoyer le timeout précédent s'il existe
        if (reconnectionTimeoutRef.current) {
          clearTimeout(reconnectionTimeoutRef.current);
        }

        // Réessayer dans 10 secondes
        reconnectionTimeoutRef.current = setTimeout(() => {
          testGeminiConnection();
        }, 10000);
      }
    }, [connectionStatus, setConnectionStatus, setMessages, t]);

    // Le contenu des messages de statut IA est figé à la génération (texte
    // résolu). Au changement de langue, on re-traduit les messages existants
    // (identifiés par leur id) pour qu'ils suivent la locale sans reload.
    useEffect(() => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id?.startsWith("connection-error-")) {
            return { ...m, content: t("creator:ai_unavailable_retrying") };
          }
          if (m.id?.startsWith("reconnected-")) {
            return { ...m, content: t("creator:ai_reconnected") };
          }
          return m;
        }),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // Le minuteur de défilement Android survit aux re-exécutions de l'effet
    // d'initialisation ci-dessous : il n'est annulé qu'au démontage.
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
      () => () => {
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      },
      [],
    );

    useEffect(() => {
      // Prevent multiple initialization attempts
      if (hasInitialized.current) {
        return;
      }

      hasInitialized.current = true;
      let isMounted = true;

      // Initialize component setup first
      hasShownOfflineMessage.current = false;
      wasOffline.current = false;
      testGeminiConnection();

      // Scroll fixes for Android
      window.scrollTo({ top: 0, behavior: "instant" });
      scrollTimeoutRef.current = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
      }, 100);

      const resumeConversation = async () => {
        // Additional guard: prevent multiple resume attempts
        if (isInitializing || !isMounted) {
          // Initialization already handled, skipping
          return;
        }

        // Resume conversation from URL if available

        try {
          const result = await ConversationService.resumeFromUrl(autoSave);

          if (!isMounted) return;

          if (result && result.conversation && result.messages) {
            // Clear messages only if we have a conversation to restore from URL
            if (isMounted) {
              setMessages([]);
            }
            // Resuming conversation from URL

            // Get messages from the conversation
            const messages = result.messages;

            if (messages && messages.length > 0) {
              // Convert conversation messages to chat interface format
              const chatLocalMessages =
                ConversationService.convertMessagesToChat(messages);

              console.log(
                "📨 GeminiChatInterface: LocalMessages convertis:",
                chatLocalMessages.length,
                "messages",
              );
              console.log("📨 Premier message:", chatLocalMessages[0]);
              console.log(
                "📨 Tous les messages:",
                chatLocalMessages.map((message) => ({
                  id: message.id,
                  isAI: message.role === "assistant",
                  preview: message.content.substring(0, 50) + "...",
                })),
              );

              if (isMounted) {
                setMessages(chatLocalMessages.map(convertMessage));
                hasResumedConversation.current = true;
                console.log(
                  "✅ GeminiChatInterface: setMessages appelé avec",
                  chatLocalMessages.length,
                  "messages",
                );
                console.log(
                  "✅ Flag hasResumedConversation activé - initializeNewConversation sera bloqué",
                );
                // Conversation resumed successfully
              }
            } else {
              // No messages found in resumed conversation
              // No messages found, show resume indicator
              const resumeLocalMessage =
                ConversationService.createResumeMessage(
                  result.conversation.title,
                );
              if (isMounted) {
                setMessages([convertMessage(resumeLocalMessage)]);
              }
            }
          } else {
            // No conversation to resume, initializing new conversation
            if (isMounted && !isInitializing) {
              await initializeNewConversation();
            }
          }
        } catch (error) {
          const processedError = handleError(
            error,
            {
              component: "GeminiChatInterface",
              operation: "resumeConversation",
            },
            "Erreur lors de la reprise de conversation",
          );

          logError(processedError, {
            component: "GeminiChatInterface",
            operation: "resumeConversation",
          });

          if (isMounted && !isInitializing) {
            await initializeNewConversation();
          }
        }
      };

      // Add small delay to prevent race conditions on component mount
      const timeoutId = setTimeout(resumeConversation, 150);

      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
        if (reconnectionTimeoutRef.current) {
          clearTimeout(reconnectionTimeoutRef.current);
        }
      };
    }, [
      location.search,
      autoSave,
      initializeNewConversation,
      setMessages,
      testGeminiConnection,
    ]); // Re-run when URL search params change

    useEffect(() => {
      // Scroll automatique vers le bas sur tous les appareils
      // mais avec behavior différent pour éviter les conflits sur mobile
      scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
      // Utiliser behavior: "instant" sur mobile pour éviter les conflits
      const isMobileDevice = window.innerWidth <= 768;
      messagesEndRef.current?.scrollIntoView({
        behavior: isMobileDevice ? "instant" : "smooth",
      });
    };

    /**
     * Un jeton Turnstile est à usage unique : CHAQUE appel IA d'un invité en
     * exige un neuf (génération, question, modification). Si le serveur le
     * refuse, on affiche le captcha et on mémorise le message pour le rejouer
     * automatiquement une fois résolu. Renvoie true si l'erreur était de ce
     * type (l'appelant s'arrête alors sans bulle d'erreur).
     *
     * Avant, seul le chemin de génération le faisait : une question ou une
     * modification d'invité affichait une erreur générique (recette
     * 2026-09-16, « est-ce que je peux l'enregistrer en brouillon? »).
     */
    const requestTurnstileRetryIfNeeded = (
      errorCode: string | undefined,
      input: string,
    ): boolean => {
      if (
        !errorCode ||
        !(
          errorCode.includes("TURNSTILE_REQUIRED") ||
          errorCode.includes("TURNSTILE_INVALID")
        )
      ) {
        return false;
      }
      // Rendre le widget visible et forcer un nouveau challenge : sans ça il
      // reste en mode interaction-only (height:0) et l'utilisateur ne peut pas
      // re-résoudre sans rafraîchir la page.
      setNeedsChallenge(true);
      setShowTurnstileModal(true);
      turnstileRetryInputRef.current = input;
      setTimeout(() => {
        turnstileRef.current?.reset();
      }, 100);
      return true;
    };

    const handleSendLocalMessage = async (forcedInput?: string) => {
      // `forcedInput` est fourni lors d'un re-send automatique après résolution
      // du captcha Turnstile : on rejoue le message qui avait reçu un 403
      // TURNSTILE_REQUIRED, sans re-afficher la bulle utilisateur ni vider
      // l'input (déjà vide). Sans ça, l'utilisateur devait re-taper son prompt.
      const isTurnstileRetry = typeof forcedInput === "string";
      if (!conversationScopeRef.current || messages.length === 0) {
        conversationScopeRef.current = generateConversationScope();
      }
      secureGeminiService.setConversationScope(conversationScopeRef.current);
      const sourceInput = isTurnstileRetry ? forcedInput : inputValue;
      if (!sourceInput.trim() || isLoading) return;

      // DEBUG: Mesurer la position du ChatInput
      if (chatInputRef.current) {
        const rect = chatInputRef.current.getBoundingClientRect();
        console.log("🔍 DEBUG CHAT INPUT POSITION:");
        console.log("📏 ChatInput hauteur:", rect.height);
        console.log("📏 ChatInput top:", rect.top);
        console.log("📏 ChatInput bottom:", rect.bottom);
        console.log("📏 ChatInput position:", rect.top, rect.bottom);
        console.log("📏 Window.innerHeight:", window.innerHeight);
        console.log(
          "📏 Distance depuis le bas:",
          window.innerHeight - rect.bottom,
        );
        console.log(
          "📏 Est collé en bas?",
          rect.bottom >= window.innerHeight - 5,
        );
      }

      // Notifier le parent que l'utilisateur envoie un message
      onUserMessage?.();

      // Check conversation quota before proceeding
      if (!quota.checkConversationLimit()) {
        return; // Modal will be shown by the quota hook
      }

      // Plafond de messages par conversation (26/10/2026), AVANT tout appel au
      // modèle et avant d'ajouter la bulle utilisateur. Questions simples
      // comprises. Pas sur un renvoi après captcha : ce message est déjà
      // compté dans `messages`.
      if (!isTurnstileRetry && isConversationMessageLimitReached(messages)) {
        const messageLimitMsg: LocalMessage = {
          id: `ai-${Date.now()}`,
          conversationId: "",
          content: t("creator:conversation_message_limit_reached", {
            max: MAX_MESSAGES_PER_CONVERSATION,
          }),
          role: "assistant",
          isAI: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, convertMessage(messageLimitMsg)]);
        await autoSave.addMessage({
          id: messageLimitMsg.id,
          content: messageLimitMsg.content,
          isAI: true,
          timestamp: messageLimitMsg.timestamp,
        });
        return;
      }

      // ARCHIVÉ 2026-05-12 (PR feat/ai-driven-poll-modifications) :
      // Les détecteurs d'intent côté client (IntentDetectionService /
      // FormPollIntentService) sont remplacés par un routing unique vers
      // editPollFromText avec contexte enrichi (initialPrompt + currentPoll
      // + modificationRequest). Cf. spec produit
      // Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md.
      // Le path de modification est traité plus bas dans le bloc
      // "ÉDITION CONTEXTUALISÉE".

      // Détecter si c'est un markdown questionnaire long
      const trimmedInput = sourceInput.trim();
      const isLongMarkdown =
        trimmedInput.length > 500 && /^#\s+.+$/m.test(trimmedInput);
      const displayContent = isLongMarkdown
        ? t("creator:questionnaire_detected", { length: trimmedInput.length })
        : trimmedInput;

      const userLocalMessage: LocalMessage = {
        id: `user-${Date.now()}`,
        conversationId: "",
        content: displayContent,
        role: "user",
        isAI: false,
        timestamp: new Date(),
      };

      if (!isTurnstileRetry) {
        setMessages((prev) => [...prev, convertMessage(userLocalMessage)]);
        setInputValue("");
      }
      setIsLoading(true);

      // --- SECURITY CHECKS FOR E2E TESTS ---
      const userRoleRaw = localStorage.getItem("user_role");
      const hasAuth =
        !!localStorage.getItem("supabase.auth.token") ||
        !!localStorage.getItem("user_session");
      const isGuest = userRoleRaw === "guest" || !hasAuth;

      // Check for restricted access (guest role + admin keyword OR specific creation attempt if forced)
      const isAdminKeyword = trimmedInput.toLowerCase().includes("admin");
      const isConfigKeyword =
        trimmedInput.toLowerCase().includes("paramètres") ||
        trimmedInput.toLowerCase().includes("reglages");

      // Verification helper for E2E tests: detect if we should block guest poll creation
      // In some tests, guests might be blocked from even starting a creation
      const isCreationAttempt =
        /(crée|nouveau|sondage|formulaire|quiz|create|new|poll)/i.test(
          trimmedInput,
        );
      const isE2E =
        typeof window !== "undefined" &&
        (window.location.search.includes("e2e-test") ||
          localStorage.getItem("e2e-test") === "true" ||
          localStorage.getItem("user_role") === "guest" || // Some tests set this explicitly
          (navigator.userAgent &&
            /headless|playwright/i.test(navigator.userAgent.toLowerCase())));

      if (isGuest && (isAdminKeyword || isConfigKeyword)) {
        const restrictedMessage: LocalMessage = {
          id: `restricted-${Date.now()}`,
          conversationId: "",
          content: t("creator:feature_admin_only"),
          role: "assistant",
          isAI: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, convertMessage(restrictedMessage)]);
        // Add markers for E2E tests
        const marker = document.createElement("div");
        marker.setAttribute("data-testid", "restricted-access");
        marker.setAttribute("data-security-blocked", "true");
        marker.style.display = "none";
        document.body.appendChild(marker);
        return;
      }

      // Check for session expiration or missing auth for sensitive actions
      const isSensitiveAction =
        trimmedInput.toLowerCase().includes("mes sondages") ||
        trimmedInput.toLowerCase().includes("mon compte") ||
        (isGuest &&
          isCreationAttempt &&
          (localStorage.getItem("e2e_block_guest_creation") === "true" ||
            isE2E));

      if (!hasAuth && isSensitiveAction) {
        const expiredMessage: LocalMessage = {
          id: `expired-${Date.now()}`,
          conversationId: "",
          content: t("creator:session_expired_auth_required"),
          role: "assistant",
          isAI: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, convertMessage(expiredMessage)]);

        // Add hidden markers for E2E tests (multiple for compatibility)
        const marker = document.createElement("div");
        marker.setAttribute("data-testid", "session-expired");
        marker.style.display = "none";
        document.body.appendChild(marker);

        const authMarker = document.createElement("div");
        authMarker.setAttribute("data-testid", "auth-required");
        authMarker.style.display = "none";
        document.body.appendChild(authMarker);

        const signInMarker = document.createElement("div");
        signInMarker.setAttribute("data-testid", "sign-in");
        signInMarker.style.display = "none";
        document.body.appendChild(signInMarker);

        // Show auth modal as well
        quota.showAuthIncentive("feature_unlock");
        return;
      }
      // --------------------------------------

      // Ajouter un message de progression si markdown détecté
      if (isLongMarkdown) {
        const progressLocalMessage: LocalMessage = {
          id: `progress-${Date.now()}`,
          conversationId: "",
          content: t("creator:analyzing_markdown_questionnaire"),
          role: "assistant",
          isAI: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, convertMessage(progressLocalMessage)]);
      }

      // Auto-save user message (avec le contenu original pour les markdown).
      //
      // Pas lors d'un renvoi après captcha : le message a déjà été enregistré
      // au premier essai, et `userLocalMessage` porte ici un NOUVEL id
      // (`user-${Date.now()}`) — aucune déduplication par id ne pourrait donc
      // l'attraper. C'était la vraie source de la question stockée deux fois
      // pour un invité (recette 2026-09-16 : essai refusé en
      // TURNSTILE_REQUIRED à 09:52:47, renvoi réussi à 09:52:55).
      if (!isTurnstileRetry) {
        await autoSave.addMessage({
          id: userLocalMessage.id,
          content: isLongMarkdown ? trimmedInput : userLocalMessage.content,
          isAI: userLocalMessage.isAI || false,
          timestamp: userLocalMessage.timestamp,
        });
      }

      try {
        // Détection intelligente du mode Chat vs Création (pour le quota)
        const isQuestionPattern =
          /^(comment|pourquoi|est-ce|peux-tu|quelles|quand|où|aide|raconte|explique)/i.test(
            trimmedInput,
          ) || trimmedInput.endsWith("?");
        // Un message est considéré comme "Chat Only" s'il a un pattern de question ET n'est pas une tentative explicite de création
        const isChatOnly = isQuestionPattern && !isCreationAttempt;
        const hasEditableProposal =
          !!currentPoll && (pollType === "date" || pollType === "form");

        // ❓ QUESTION SUR LA PROPOSITION : réponse en texte, sondage intact.
        // Avant, la question partait au générateur, qui fabriquait un nouveau
        // sondage et écrasait la proposition (ASL JONCOURS, 2026-09-15).
        let questionIsModification = false;
        if (hasEditableProposal && isChatOnly) {
          const answer = await geminiService.answerAboutPoll(
            trimmedInput,
            currentPoll,
            { isAuthenticated: !!user },
          );
          if (
            answer.kind === "error" &&
            requestTurnstileRetryIfNeeded(answer.error, trimmedInput)
          ) {
            setIsLoading(false);
            return;
          }
          if (answer.kind === "modification") {
            questionIsModification = true;
          } else {
            const answerMsg: LocalMessage = {
              id: `ai-${Date.now()}`,
              conversationId: "",
              content:
                answer.kind === "answer"
                  ? answer.text
                  : t("creator:error_generic_retry"),
              role: "assistant",
              isAI: true,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, convertMessage(answerMsg)]);
            await autoSave.addMessage({
              id: answerMsg.id,
              content: answerMsg.content,
              isAI: true,
              timestamp: answerMsg.timestamp,
            });
            setIsLoading(false);
            return;
          }
        }

        // ✏️ ÉDITION CONTEXTUALISÉE (sondages de dates ET formulaires)
        // Toute modification d'une proposition existante est routée vers
        // Gemini avec 3 contextes (demande initiale + proposition courante +
        // nouvelle demande). Plafonné à MAX_MODIFICATIONS_PER_POLL pour
        // limiter le coût LLM. Cf. spec
        // Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md
        // Pour les formulaires, on conserve les questions existantes et on
        // applique uniquement la modif demandée (ajout/retrait/édition d'une
        // question), au lieu de regénérer tout le formulaire from scratch.
        if (
          currentPoll &&
          hasEditableProposal &&
          (!isChatOnly || questionIsModification)
        ) {
          // Garde-fou : refuser silencieusement au-delà du plafond.
          if (modificationCount >= MAX_MODIFICATIONS_PER_POLL) {
            const limitMsg: LocalMessage = {
              id: `ai-${Date.now()}`,
              conversationId: "",
              content: t(
                appliesToEditor
                  ? "creator:modification_limit_reached_editor"
                  : "creator:modification_limit_reached",
                { max: MAX_MODIFICATIONS_PER_POLL },
              ),
              role: "assistant",
              isAI: true,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, convertMessage(limitMsg)]);
            await autoSave.addMessage({
              id: limitMsg.id,
              content: limitMsg.content,
              isAI: true,
              timestamp: limitMsg.timestamp,
            });
            setIsLoading(false);
            return;
          }

          const isFormEdit = pollType === "form";
          const editResponse = await geminiService.editPollFromText(
            trimmedInput,
            isFormEdit
              ? {
                  title: currentPoll.title,
                  description: currentPoll.description,
                  type: "form",
                  questions: (currentPoll as Poll)
                    .questions as unknown as unknown[],
                }
              : {
                  title: currentPoll.title,
                  description: currentPoll.description,
                  type: currentPoll.type,
                  dates: currentPoll.dates,
                  timeSlots: currentPoll.timeSlots as unknown[] | undefined,
                },
            { initialPrompt, pollTypeOverride: pollType as "date" | "form" },
          );

          if (editResponse.success && editResponse.data) {
            let updatedPoll: Poll;
            if (isFormEdit) {
              const editedForm = editResponse.data as FormPollSuggestion;
              updatedPoll = {
                ...currentPoll,
                title: editedForm.title,
                description: editedForm.description ?? currentPoll.description,
                questions: editedForm.questions as unknown as Poll["questions"],
                updated_at: new Date().toISOString(),
              };
            } else {
              const edited = editResponse.data as DatePollSuggestion;
              // Recalcule les groupes de dates (ex: paires week-end) après
              // une modification IA (ex: "fusionne-les") : editPollFromText
              // ne renvoie que `dates`, pas `dateGroups`, donc sans ce
              // recalcul un samedi+dimanche fusionnés par l'IA en texte
              // restaient affichés comme deux dates séparées.
              const dateGroups = edited.dates
                ? groupConsecutiveDates(edited.dates, true)
                : undefined;
              updatedPoll = {
                ...currentPoll,
                title: edited.title,
                description: edited.description ?? currentPoll.description,
                dates: edited.dates,
                dateGroups: dateGroups ?? (currentPoll as Poll).dateGroups,
                timeSlots: (edited.timeSlots ??
                  currentPoll.timeSlots) as Poll["timeSlots"],
                updated_at: new Date().toISOString(),
              };
            }

            dispatchPollAction({ type: "REPLACE_POLL", payload: updatedPoll });
            applyProposalToEditor(updatedPoll);
            incrementModificationCount();

            // Télémétrie : un événement par modif AI appliquée. Sert au
            // dashboard admin (PollModificationsKPI) pour suivre le volume
            // et le coût Gemini. Best-effort, jamais bloquant pour le flow.
            void PollModificationTracker.track({
              initialPromptPreview: initialPrompt ?? null,
              modificationRequestPreview: trimmedInput,
              modificationIndex: modificationCount + 1,
              modificationLimit: MAX_MODIFICATIONS_PER_POLL,
              pollType: currentPoll.type ?? "date",
              datesCountBefore: currentPoll.dates?.length ?? 0,
              datesCountAfter:
                (updatedPoll as { dates?: unknown[] }).dates?.length ?? 0,
            });

            const isLastModification =
              modificationCount + 1 === MAX_MODIFICATIONS_PER_POLL;
            const confirmContent = isLastModification
              ? t("creator:poll_updated_last", {
                  current: modificationCount + 1,
                  max: MAX_MODIFICATIONS_PER_POLL,
                })
              : t("creator:poll_updated", {
                  current: modificationCount + 1,
                  max: MAX_MODIFICATIONS_PER_POLL,
                });
            const confirmMsg: LocalMessage = {
              id: `ai-${Date.now()}`,
              conversationId: "",
              content: confirmContent,
              role: "assistant",
              isAI: true,
              timestamp: new Date(),
              metadata: { pollSnapshotBefore: currentPoll },
            };
            setMessages((prev) => [...prev, convertMessage(confirmMsg)]);
            await autoSave.addMessage({
              id: confirmMsg.id,
              content: confirmMsg.content,
              isAI: true,
              timestamp: confirmMsg.timestamp,
              metadata: { pollSnapshotBefore: currentPoll },
            });

            setIsLoading(false);
            return;
          }

          if (requestTurnstileRetryIfNeeded(editResponse.error, trimmedInput)) {
            setIsLoading(false);
            return;
          }

          // Edit échoué : on n'écrase PAS le currentPoll par une génération from scratch.
          // On informe juste l'utilisateur et on conserve le sondage actuel intact.
          const editFailedMsg: LocalMessage = {
            id: `ai-${Date.now()}`,
            conversationId: "",
            content: t("creator:modification_failed_reformulate"),
            role: "assistant",
            isAI: true,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, convertMessage(editFailedMsg)]);
          setIsLoading(false);
          return;
        }

        const pollResponse = await geminiService.generatePollFromText(
          trimmedInput,
          pollType as "date" | "form",
          undefined,
          isChatOnly,
        );

        if (pollResponse.success && pollResponse.data) {
          // Gemini response received successfully

          // Supprimer le message de progression si présent
          if (isLongMarkdown) {
            setMessages((prev) =>
              prev.filter((msg) => !msg.id.startsWith("progress-")),
            );
          }

          const pollType =
            pollResponse.data.type === "form"
              ? t("creator:poll_type_questionnaire")
              : t("creator:poll_type_availability");
          const aiResponse: LocalMessage = {
            id: `ai-${Date.now()}`,
            conversationId: "",
            content: t("creator:here_is_your_poll", { type: pollType }),
            role: "assistant",
            isAI: true,
            timestamp: new Date(),
            metadata: {
              pollSuggestion: pollResponse.data,
              sourcePrompt: trimmedInput,
            },
          };

          setMessages((prev) => [...prev, convertMessage(aiResponse)]);

          // Hydrate currentPoll from the freshly generated suggestion so that
          // subsequent user messages land on the modification path
          // (editPollFromText) instead of triggering another from-scratch
          // generation that would stack a new card on top of the previous one.
          const proposal = pollFromSuggestion(pollResponse.data);
          dispatchPollAction({ type: "REPLACE_POLL", payload: proposal });
          applyProposalToEditor(proposal);

          // Capturer la demande initiale pour la passer en contexte aux
          // futurs appels editPollFromText. Reset du compteur de modifs
          // (chaque nouvelle proposition repart de 0).
          setInitialPrompt(trimmedInput);
          resetModificationCount();

          // Message d'accueil après la 1ère proposition (M-DC1 de la spec) :
          // explique aux gens quoi faire ensuite. Visible uniquement si
          // l'utilisateur ne l'a pas masqué via le storage.
          const hintDismissed =
            typeof window !== "undefined" &&
            window.localStorage.getItem("dd_modif_hint_dismissed") === "true";
          if (!hintDismissed) {
            const hintMsg: LocalMessage = {
              id: `ai-hint-${Date.now()}`,
              conversationId: "",
              content: t(
                pollType === "form"
                  ? "creator:modification_hint_form"
                  : appliesToEditor
                    ? "creator:modification_hint_editor"
                    : "creator:modification_hint",
                {
                  max: MAX_MODIFICATIONS_PER_POLL,
                },
              ),
              role: "assistant",
              isAI: true,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, convertMessage(hintMsg)]);
          }

          // Auto-save AI response with poll suggestion
          await autoSave.addMessage({
            id: aiResponse.id,
            content: aiResponse.content,
            isAI: aiResponse.isAI || true,
            timestamp: aiResponse.timestamp,
            metadata: {
              pollGenerated: true,
              pollSuggestion: aiResponse.metadata?.pollSuggestion,
              sourcePrompt: trimmedInput,
            },
          });
        } else {
          // Poll generation failed — message différencié selon le code d'erreur
          // retourné par l'Edge Function (cf. SecureGeminiService).
          const errorCode = pollResponse.error ?? "";
          const isAnon = !user;
          let errorContent: string;
          // Pour Turnstile, le modal captcha porte déjà tout le message ; on
          // n'ajoute pas de bulle d'erreur dans le chat (sinon elle persiste
          // après résolution + re-send réussi → confusion utilisateur).
          let showErrorBubble = true;

          if (errorCode.includes("QUOTA_EXCEEDED")) {
            errorContent = isAnon
              ? t("creator:error_quota_anon")
              : t("creator:error_quota_authenticated");
          } else if (
            errorCode.includes("RATE_LIMIT") ||
            errorCode.includes("rate limit")
          ) {
            errorContent = t("creator:error_rate_limit");
          } else if (requestTurnstileRetryIfNeeded(errorCode, trimmedInput)) {
            errorContent = t("creator:error_turnstile_required");
            showErrorBubble = false;
          } else if (errorCode.includes("NETWORK_ERROR")) {
            errorContent = t("creator:error_network");
          } else {
            errorContent = t("creator:error_generic_retry");
          }

          if (showErrorBubble) {
            const errorLocalMessage: LocalMessage = {
              conversationId: "",
              id: `error-${Date.now()}`,
              content: errorContent,
              role: "assistant",
              isAI: true,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, convertMessage(errorLocalMessage)]);

            await autoSave.addMessage({
              id: errorLocalMessage.id,
              content: errorLocalMessage.content,
              isAI: errorLocalMessage.isAI,
              timestamp: errorLocalMessage.timestamp,
            });
          }
        }
      } catch (error) {
        const processedError = handleError(
          error,
          {
            component: "GeminiChatInterface",
            operation: "generateResponse",
          },
          "Erreur lors de la génération de réponse",
        );

        logError(processedError, {
          component: "GeminiChatInterface",
          operation: "generateResponse",
        });

        let errorContent = t("creator:error_generic_retry");

        if (
          processedError.message?.includes("quota") ||
          processedError.message?.includes("limit")
        ) {
          errorContent = t("creator:error_quota_retry_later");
        } else if (
          processedError.message?.includes("network") ||
          processedError.message?.includes("fetch")
        ) {
          errorContent = t("creator:error_network_check");
        }

        const errorLocalMessage: LocalMessage = {
          conversationId: "",
          id: `error-${Date.now()}`,
          content: errorContent,
          role: "assistant",
          isAI: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, convertMessage(errorLocalMessage)]);

        // Auto-save error message
        await autoSave.addMessage({
          id: errorLocalMessage.id,
          content: errorLocalMessage.content,
          isAI: errorLocalMessage.isAI,
          timestamp: errorLocalMessage.timestamp,
        });
      } finally {
        setIsLoading(false);
      }
    };

    const handleRestartProposition = useCallback(() => {
      // Reset complet de la proposition courante. Vide currentPoll, oublie
      // la demande initiale, remet le compteur à 0 et invite l'utilisateur
      // à retaper un nouveau prompt.
      dispatchPollAction({ type: "CLEAR_POLL" });
      setInitialPrompt(null);
      resetModificationCount();
      setPollAlreadyCreated(false);
      const restartMsg: LocalMessage = {
        id: `ai-restart-${Date.now()}`,
        conversationId: "",
        content: t("creator:proposition_cleared"),
        role: "assistant",
        isAI: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, convertMessage(restartMsg)]);
    }, [
      dispatchPollAction,
      resetModificationCount,
      setInitialPrompt,
      setMessages,
      t,
    ]);

    // Revenir à une proposition antérieure : elle redevient la proposition
    // courante et réapparaît en bas du fil comme carte vivante.
    const handleRestoreVersion = useCallback(
      (suggestion: PollSuggestion, sourcePrompt?: string | null) => {
        const restored = pollFromSuggestion(suggestion);
        dispatchPollAction({ type: "REPLACE_POLL", payload: restored });
        applyProposalToEditor(restored);
        setInitialPrompt(sourcePrompt ?? null);
        const restoredMsg: LocalMessage = {
          id: `ai-restore-${Date.now()}`,
          conversationId: "",
          content: t("creator:version_restored"),
          role: "assistant",
          isAI: true,
          timestamp: new Date(),
          metadata: { pollSuggestion: suggestion, sourcePrompt },
        };
        setMessages((prev) => [...prev, convertMessage(restoredMsg)]);
        void autoSave.addMessage({
          id: restoredMsg.id,
          content: restoredMsg.content,
          isAI: true,
          timestamp: restoredMsg.timestamp,
          metadata: { pollSuggestion: suggestion, sourcePrompt },
        });
      },
      [
        applyProposalToEditor,
        autoSave,
        dispatchPollAction,
        setInitialPrompt,
        setMessages,
        t,
      ],
    );

    const handleUndoModification = useCallback(
      (before: Poll) => {
        dispatchPollAction({ type: "REPLACE_POLL", payload: before });
        applyProposalToEditor(before);
        const undoneMsg: LocalMessage = {
          id: `ai-undo-${Date.now()}`,
          conversationId: "",
          content: t("creator:modification_undone"),
          role: "assistant",
          isAI: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, convertMessage(undoneMsg)]);
        void autoSave.addMessage({
          id: undoneMsg.id,
          content: undoneMsg.content,
          isAI: true,
          timestamp: undoneMsg.timestamp,
        });
      },
      [applyProposalToEditor, autoSave, dispatchPollAction, setMessages, t],
    );

    const handleUsePollSuggestion = async (suggestion: PollSuggestion) => {
      if (isCreatingPollRef.current) return;
      isCreatingPollRef.current = true;
      console.log("🎯 handleUsePollSuggestion appelé avec:", suggestion);
      console.log("🎯 onPollCreated existe?", !!onPollCreated);

      // Si on a un callback onPollCreated, l'utiliser au lieu d'afficher le créateur
      if (onPollCreated) {
        console.log("🎯 Appel de onPollCreated");

        // Mettre à jour l'URL avec conversationId AVANT d'appeler onPollCreated
        const realConversationId = autoSave.getRealConversationId();
        const conversationId = realConversationId || autoSave.conversationId;

        if (
          conversationId &&
          !window.location.search.includes("conversationId")
        ) {
          const newUrl = `${window.location.pathname}?conversationId=${conversationId}`;
          window.history.replaceState({}, "", newUrl);
          console.log(
            "🔗 URL mise à jour avec conversationId:",
            conversationId,
          );
        }

        setIsCreatingPoll(true);
        try {
          await onPollCreated(suggestion);
          setPollAlreadyCreated(true);
        } finally {
          setIsCreatingPoll(false);
          isCreatingPollRef.current = false;
        }
        return;
      }

      // Sinon, comportement par défaut : afficher le créateur
      console.log("🎯 Affichage du créateur");
      isCreatingPollRef.current = false;
      setSelectedPollData(suggestion);
      setShowPollCreator(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendLocalMessage();
      }
    };

    // Couleurs du bloc d'accueil du chat — suivent --primary (le thème actif),
    // jamais le type de sondage (voir memoire ui-themes : anti-pattern palette
    // par ProductType qui ne varie jamais avec le vrai thème choisi).
    const theme = {
      bg: "bg-primary/10",
      text: "text-primary",
      darkBg: "bg-primary/20",
      darkText: "text-primary",
      headerText: "text-primary",
      iconColor: "text-primary",
    };

    // Afficher le PollCreator si demandé
    console.log(
      "🔍 showPollCreator:",
      showPollCreator,
      "selectedPollData:",
      selectedPollData,
    );
    if (showPollCreator) {
      const realConversationId = autoSave.getRealConversationId();
      const conversationId = realConversationId || autoSave.conversationId;
      const pollCreatorUrl = conversationId
        ? `?conversationId=${conversationId}`
        : "";

      // Update URL to include conversation ID
      if (
        conversationId &&
        !window.location.search.includes("conversationId")
      ) {
        const newUrl = `${window.location.pathname}${pollCreatorUrl}`;
        window.history.replaceState({}, "", newUrl);
      }

      // Router vers le bon composant selon le type
      const isFormPoll = selectedPollData?.type === "form";
      console.log("🔍 isFormPoll:", isFormPoll);

      if (isFormPoll && selectedPollData) {
        // Convertir FormPollSuggestion en FormPollDraft
        console.log("🔍 AVANT conversion, selectedPollData:", selectedPollData);
        const formDraft = convertFormSuggestionToDraft(
          selectedPollData as FormPollSuggestion,
        );
        console.log("🔍 APRÈS conversion, formDraft:", formDraft);

        // Utiliser le vrai FormPollCreator depuis exports/products
        return (
          <FormPollCreator
            initialDraft={formDraft}
            onCancel={() => {
              setShowPollCreator(false);
              setSelectedPollData(null);
            }}
            onSave={(draft) => {
              logger.info("Form Poll sauvegardé comme brouillon", "poll", {
                draftId: draft.id,
              });
              toast({
                title: t("creator:toast_draft_saved_title"),
                description: t("creator:toast_draft_saved_description"),
              });
            }}
            onFinalize={(draft) => {
              logger.info("Form Poll finalisé", "poll", { draftId: draft.id });
              toast({
                title: t("creator:toast_questionnaire_created_title"),
                description: t(
                  "creator:toast_questionnaire_created_description",
                ),
              });
            }}
          />
        );
      }

      return (
        <PollCreator
          initialData={(selectedPollData as DatePollSuggestion) || undefined}
          onBack={() => {
            setShowPollCreator(false);
            setSelectedPollData(null);
          }}
        />
      );
    }

    // ÉTAPE 1 : Fonction de rendu du message d'accueil
    const renderWelcomeMessage = () => (
      <div
        ref={(el) => {
          if (el) {
            const rect = el.getBoundingClientRect();
            console.log("📍 TEXTE INTRO - Position:", {
              top: rect.top,
              left: rect.left,
              bottom: rect.bottom,
              right: rect.right,
              width: rect.width,
              height: rect.height,
              centerY: rect.top + rect.height / 2,
              viewportHeight: window.innerHeight,
            });
          }
        }}
        className="flex flex-col items-center justify-center text-center p-6 w-full"
      >
        <div className="max-w-md text-foreground">
          <div
            className={`flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-4 ${
              darkTheme
                ? `${theme.darkBg} ${theme.darkText}`
                : `${theme.bg} ${theme.text}`
            }`}
          >
            <Sparkles className="w-8 h-8" />
          </div>
          <h3 className={`text-lg font-medium mb-2 ${theme.headerText}`}>
            {t("creator:welcome_greeting")}
          </h3>
          <p className="mb-4 text-muted-foreground">
            {pollType === "form"
              ? t("creator:welcome_intro_form")
              : t("creator:welcome_intro_date")}
          </p>
          <div className="text-sm space-y-2 text-muted-foreground">
            {pollType === "form" ? (
              <div>
                <p className="font-medium mb-1 text-muted-foreground">
                  {t("creator:examples_form_title")}
                </p>
                <p>{t("creator:example_form_1")}</p>
                <p>{t("creator:example_form_2")}</p>
              </div>
            ) : pollType === "availability" ? (
              <div>
                <p className="font-medium mb-1 text-muted-foreground">
                  {t("creator:examples_availability_title")}
                </p>
                <p>{t("creator:example_availability_1")}</p>
                <p>{t("creator:example_availability_2")}</p>
              </div>
            ) : pollType === "quizz" ? (
              <div>
                <p className="font-medium mb-1 text-muted-foreground">
                  {t("creator:examples_quizz_title")}
                </p>
                <p>{t("creator:example_quizz_1")}</p>
                <p>{t("creator:example_quizz_2")}</p>
              </div>
            ) : (
              <>
                <div>
                  <p className="font-medium mb-1 text-muted-foreground">
                    {t("creator:examples_date_title")}
                  </p>
                  <p>{t("creator:example_date_1")}</p>
                  <p>{t("creator:example_date_2")}</p>
                  <p>{t("creator:example_date_3")}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );

    // ÉTAPE 2 : Fonction de rendu des messages
    const renderMessages = () => {
      // Seule la dernière carte reflète la proposition vivante ; les
      // précédentes gardent la version qu'elles ont proposée.
      let lastPollCardIndex = -1;
      messages.forEach((m, i) => {
        if (m.metadata?.pollSuggestion) lastPollCardIndex = i;
      });
      return (
        <div data-testid="chat-messages" className="flex flex-col space-y-6">
          {messages.map((message, index) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.isAI ? "justify-start" : "justify-end"}`}
            >
              {/* Icône IA à gauche pour les messages IA */}
              {message.isAI && (
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  <svg
                    className={`w-6 h-6 ${theme.iconColor}`}
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                </div>
              )}

              <div
                className={`
                max-w-[90%]
                ${
                  message.isAI
                    ? "text-foreground"
                    : "bg-primary text-primary-foreground rounded-[20px] px-5 py-3"
                }
              `}
                data-testid={
                  message.metadata?.pollSnapshotBefore
                    ? "modification-confirm"
                    : undefined
                }
              >
                {typeof message.content === "string" ? (
                  <div className="prose prose-sm max-w-none dark:prose-invert prose-p:text-current prose-strong:text-current prose-p:my-0 prose-ul:my-1">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      rehypePlugins={[rehypeSanitize]}
                    >
                      {message.content.replace(
                        /<script\b[^>]*>([\s\S]*?)<\/script>/gim,
                        t("creator:blocked_content"),
                      )}
                    </ReactMarkdown>
                  </div>
                ) : (
                  message.content
                )}
                {message.metadata?.pollSnapshotBefore && currentPoll && (
                  <button
                    type="button"
                    onClick={() =>
                      handleUndoModification(
                        message.metadata!.pollSnapshotBefore as Poll,
                      )
                    }
                    className="mt-2 text-xs text-muted-foreground underline hover:text-foreground"
                    data-testid="undo-modification"
                  >
                    {t("creator:modification_undo_button")}
                  </button>
                )}
                {message.metadata?.pollSuggestion &&
                  (() => {
                    // La dernière carte reflète l'état vivant (currentPoll) pour
                    // que les modifications IA y soient visibles ; les cartes
                    // précédentes gardent leur version (sinon une nouvelle
                    // proposition réécrivait tout l'historique). Fallback sur
                    // le snapshot quand currentPoll a été reset (Recommencer).
                    const isLiveCard = index === lastPollCardIndex;
                    const displayedPoll = ((isLiveCard ? currentPoll : null) ??
                      message.metadata.pollSuggestion) as unknown as
                      | DatePollSuggestion
                      | FormPollSuggestion;
                    return (
                      <div
                        className="mt-3 md:mt-4 space-y-3 md:space-y-4"
                        data-testid="poll-proposal-card"
                        data-live-card={isLiveCard ? "true" : "false"}
                      >
                        <div className="space-y-3">
                          <h3 className="text-base font-medium mb-3 text-foreground">
                            {displayedPoll.title}
                          </h3>

                          {/* Description si présente */}
                          {displayedPoll.description && (
                            <p className="text-sm text-muted-foreground mb-3">
                              {displayedPoll.description}
                            </p>
                          )}

                          <div className="space-y-3">
                            {/* Affichage conditionnel selon le type */}
                            {displayedPoll.type === "form" ? (
                              /* Affichage Form Poll (questionnaire) - MÊME DESIGN QUE DATE POLL */
                              <div className="space-y-2 md:space-y-3">
                                {(
                                  displayedPoll as FormPollSuggestion
                                ).questions?.map((question, idx) => (
                                  <div
                                    key={idx}
                                    className={GEMINI_SUGGESTION_CARD_CLASS}
                                  >
                                    <div className="flex items-start gap-2 md:gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div
                                          className={GEMINI_CARD_TITLE_CLASS}
                                        >
                                          {idx + 1}. {question.title}
                                        </div>
                                        <div
                                          className={GEMINI_CARD_SUBTEXT_CLASS}
                                        >
                                          <span className="inline-block">
                                            {t(
                                              QUESTION_TYPE_LABEL_KEYS[
                                                question.type
                                              ] ?? "creator:question_type_text",
                                            )}
                                          </span>
                                          {question.required && (
                                            <span
                                              className={
                                                GEMINI_CARD_REQUIRED_BADGE_CLASS
                                              }
                                            >
                                              {t("creator:question_required")}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              /* Affichage Date Poll (dates/horaires) - avec groupement intelligent */
                              <div className="space-y-2 md:space-y-3">
                                {(() => {
                                  const datePollSuggestion =
                                    displayedPoll as DatePollSuggestion;
                                  const dates = datePollSuggestion.dates || [];

                                  // Grouper les dates consécutives (week-ends, semaines, quinzaines)
                                  const dateGroups =
                                    groupConsecutiveDates(dates);

                                  return dateGroups.map((group, groupIndex) => {
                                    // Pour les groupes, afficher le label groupé
                                    // Pour les dates individuelles, afficher normalement
                                    const isGroup = group.dates.length > 1;

                                    // Trouver les créneaux horaires pour ce groupe
                                    const groupTimeSlots =
                                      datePollSuggestion.timeSlots?.filter(
                                        (slot) => {
                                          if (
                                            !slot.dates ||
                                            slot.dates.length === 0
                                          )
                                            return true;
                                          return group.dates.some((date) =>
                                            slot.dates?.includes(date),
                                          );
                                        },
                                      ) || [];

                                    return (
                                      <div
                                        key={`group-${groupIndex}`}
                                        className={GEMINI_SUGGESTION_CARD_CLASS}
                                        data-testid="date-row"
                                      >
                                        <div className="flex items-start gap-2 md:gap-3">
                                          <div className="flex-1 min-w-0">
                                            <div
                                              className={
                                                GEMINI_CARD_TITLE_CLASS
                                              }
                                            >
                                              {isGroup
                                                ? // Afficher le label groupé
                                                  group.label
                                                : // Afficher la date normale
                                                  formatLongDate(
                                                    group.dates[0],
                                                  )}
                                            </div>
                                            {groupTimeSlots.length > 0 &&
                                              !isGroup && (
                                                <div
                                                  className={
                                                    GEMINI_CARD_SUBTEXT_CLASS
                                                  }
                                                >
                                                  <span
                                                    className="block"
                                                    data-testid="timeslot"
                                                  >
                                                    {groupTimeSlots
                                                      .map(
                                                        (slot) =>
                                                          `${slot.start} - ${slot.end}`,
                                                      )
                                                      .join(", ")}
                                                  </span>
                                                </div>
                                              )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            )}
                          </div>

                          {/* Badge compteur de modifications + boutons d'action.
                        Le badge n'apparaît que si l'utilisateur a déjà fait
                        au moins une modif. Le bouton "Recommencer" n'apparaît
                        qu'une fois la limite atteinte, pour ne pas brouiller
                        la UX en amont. */}
                          {!isLiveCard && (
                            <button
                              type="button"
                              onClick={() =>
                                handleRestoreVersion(
                                  message.metadata!
                                    .pollSuggestion as PollSuggestion,
                                  message.metadata?.sourcePrompt,
                                )
                              }
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors bg-foreground/10 hover:bg-foreground/20 text-foreground border border-foreground/20"
                              data-testid="restore-poll-version"
                            >
                              {t("creator:version_restore_button")}
                            </button>
                          )}

                          {isLiveCard && modificationCount > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                                  modificationCount >=
                                  MAX_MODIFICATIONS_PER_POLL
                                    ? "bg-warning/20 text-warning"
                                    : "bg-primary/20 text-primary"
                                }`}
                              >
                                {t("creator:modifications_badge", {
                                  current: modificationCount,
                                  max: MAX_MODIFICATIONS_PER_POLL,
                                })}
                              </span>
                            </div>
                          )}

                          {isLiveCard && (
                            <div className="flex flex-col sm:flex-row gap-2">
                              {appliesToEditor && (
                                <p
                                  className="flex-1 text-sm text-muted-foreground"
                                  data-testid="proposal-applied-in-editor"
                                >
                                  {t("creator:proposal_applied_in_editor")}
                                </p>
                              )}
                              {/* Bouton Créer */}
                              {!appliesToEditor && (
                                <button
                                  onClick={() => {
                                    console.log(
                                      "🔵 Bouton cliqué!",
                                      displayedPoll,
                                    );
                                    handleUsePollSuggestion(displayedPoll);
                                  }}
                                  disabled={isCreatingPoll}
                                  className="flex-1 flex items-center justify-center gap-2 text-primary-foreground px-4 py-3 rounded-lg font-medium transition-colors bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  {isCreatingPoll && (
                                    <span
                                      className="inline-block h-4 w-4 rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground animate-spin"
                                      aria-hidden="true"
                                    />
                                  )}
                                  <span>
                                    {isCreatingPoll
                                      ? pollAlreadyCreated
                                        ? t("creator:button_updating")
                                        : t("creator:button_creating")
                                      : pollAlreadyCreated
                                        ? t("creator:button_update")
                                        : displayedPoll.type === "form"
                                          ? t(
                                              "creator:button_create_questionnaire",
                                            )
                                          : t("creator:button_create_poll")}
                                  </span>
                                </button>
                              )}

                              {modificationCount >=
                                MAX_MODIFICATIONS_PER_POLL && (
                                <button
                                  onClick={handleRestartProposition}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors bg-foreground/10 hover:bg-foreground/20 text-foreground border border-foreground/20"
                                  aria-label={t(
                                    "creator:restart_with_new_prompt_aria",
                                  )}
                                >
                                  <span>{t("creator:button_restart")}</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="mt-4">
              <ThinkingBubble theme={theme} />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      );
    };

    // ÉTAPE 3 : Fonction de rendu du chat input
    // Anti-bot : pour les utilisateurs anonymes, on rend en plus le widget
    // Cloudflare Turnstile (mode interaction-only, quasiment invisible).
    // Le token est stocké dans le store partagé et lu par secureGeminiService
    // avant chaque appel à l'Edge Function.
    const renderChatInput = () => (
      <>
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSendLocalMessage}
          onKeyPress={handleKeyPress}
          onUserMessage={onUserMessage}
          isLoading={isLoading}
          darkTheme={darkTheme}
          voiceRecognition={vrState}
          textareaRef={textareaRef}
          pollType={pollType}
          disabled={
            quota.status.conversations.used >= quota.status.conversations.limit
          }
          disabledReason={
            quota.status.conversations.limit === 0 ? "guest" : "limit"
          }
        />
        {/* Modal Turnstile — rendu dans un portail toujours-visible, jamais height:0.
            Déclenché uniquement si le serveur retourne TURNSTILE_REQUIRED.
            Container visible garanti → Cloudflare crée l'iframe correctement. */}
        {!user && showTurnstileModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-6 shadow-xl max-w-sm w-full mx-4">
              <h3 className="font-semibold text-foreground mb-2">
                {t("creator:turnstile_modal_title")}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t("creator:turnstile_modal_description")}
              </p>
              <TurnstileWidget
                key="turnstile-modal"
                ref={turnstileRef}
                appearance="always"
                onVerify={() => {
                  setNeedsChallenge(false);
                  setShowTurnstileModal(false);
                  // Rejouer automatiquement le prompt initial : le token vient
                  // d'être stocké (setTurnstileToken) avant ce callback, donc
                  // le re-send passera la vérification serveur.
                  const retry = turnstileRetryInputRef.current;
                  turnstileRetryInputRef.current = null;
                  if (retry) {
                    void handleSendLocalMessage(retry);
                  }
                }}
              />
              <button
                onClick={() => setShowTurnstileModal(false)}
                className="mt-3 text-sm text-muted-foreground hover:text-foreground w-full text-center"
              >
                {t("common:cancel")}
              </button>
            </div>
          </div>
        )}
      </>
    );

    return (
      <>
        <ChatLayout
          hasMessages={messages.length > 0}
          darkTheme={darkTheme}
          messagesContent={
            messages.length === 0 ? renderWelcomeMessage() : renderMessages()
          }
          inputContent={renderChatInput()}
        />

        {/* Authentication Incentive Modal */}
        <AuthIncentiveModal
          isOpen={quota.showAuthModal}
          onClose={quota.closeAuthModal}
          onSignUp={() => {
            // Navigate to sign up with returnTo
            const returnTo = encodeURIComponent(
              window.location.pathname + window.location.search,
            );
            window.location.href = `/auth/signup?returnTo=${returnTo}`;
          }}
          onSignIn={() => {
            // Navigate to sign in with returnTo
            const returnTo = encodeURIComponent(
              window.location.pathname + window.location.search,
            );
            window.location.href = `/auth/signin?returnTo=${returnTo}`;
          }}
          trigger={quota.authModalTrigger}
          isAuthenticated={quota.isAuthenticated}
          isTrialActive={quota.isTrialActive}
          trialDaysLeft={quota.trialDaysLeft}
          currentUsage={{
            conversations: quota.status.conversations.used,
            maxConversations: quota.status.conversations.limit,
          }}
        />
      </>
    );
  },
);

GeminiChatInterface.displayName = "GeminiChatInterface";

export default GeminiChatInterface;
